import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
    AcademicCapIcon,
    ShieldCheckIcon,
    TrophyIcon,
    ChartBarIcon,
    BookOpenIcon,
    SparklesIcon,
    UserCircleIcon,
    EnvelopeIcon,
    CalendarIcon,
    ArrowTrendingUpIcon,
    FireIcon,
    StarIcon,
    CheckCircleIcon,
    Cog6ToothIcon
} from '@heroicons/react/24/outline';

const ProfilePage = () => {
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('courses');
    const { token, user } = useContext(AuthContext);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!token) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const config = { headers: { 'x-auth-token': token } };
                const res = await axios.get(`${API_BASE_URL}/api/users/me/profile`, config);
                setProfileData(res.data);
            } catch (err) {
                toast.error('Profile information could not be loaded.');
                console.error('Profile error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [token]);

    const calculateProgress = (enrollment) => {
        // E�er kurs tamamland�ysa (sertifika al�nd�ysa) direkt 100 d�nd�r
        if (enrollment.completed || enrollment.certificateEarned) {
            return 100;
        }
        if (!enrollment.course) {
            return 0;
        }
        
        // Progress hesaplama: %80 okuma + %20 quiz
        const totalPages = enrollment.course.readingPages?.length || 0;
        const completedPagesCount = enrollment.completedPages?.length || 0;
        
        // Okuma progress'i (max %80)
        let readingProgress = 0;
        if (totalPages > 0) {
            readingProgress = (completedPagesCount / totalPages) * 80;
        }
        
        // Quiz progress'i (max %20) - sadece quiz varsa ve passing score al�nm��sa
        let quizProgress = 0;
        if (enrollment.course.quizId && enrollment.quizScore !== undefined && enrollment.quizScore !== null) {
            // Quiz'i ge�tiyse %20 ekle
            quizProgress = 20;
        }
        
        return Math.round(readingProgress + quizProgress);
    };

    const calculateOverallProgress = () => {
        if (!profileData || !profileData.enrollments || profileData.enrollments.length === 0) {
            return 0;
        }
        const validEnrollments = profileData.enrollments.filter(e => e.course);
        if (validEnrollments.length === 0) return 0;
        const totalProgress = validEnrollments.reduce((acc, enroll) => acc + calculateProgress(enroll), 0);
        return Math.round(totalProgress / validEnrollments.length);
    };

    const getCompletedCoursesCount = () => {
        if (!profileData || !profileData.enrollments) return 0;
        return profileData.enrollments.filter(enroll => enroll.course && (enroll.completed || enroll.certificateEarned)).length;
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin':
                return 'from-red-500 to-pink-500';
            case 'editor':
                return 'from-yellow-500 to-orange-500';
            default:
                return 'from-blue-500 to-cyan-500';
        }
    };

    const getRoleBadge = (role) => {
        switch (role) {
            case 'admin':
                return { icon: ShieldCheckIcon, label: 'Administrator', color: 'bg-red-500' };
            case 'editor':
                return { icon: BookOpenIcon, label: 'Content Editor', color: 'bg-yellow-500' };
            default:
                return { icon: UserCircleIcon, label: 'Student', color: 'bg-blue-500' };
        }
    };

    if (loading) return <Spinner />;
    if (!profileData || !user) {
        return (
            <div className="card p-8 text-center">
                <UserCircleIcon className="h-16 w-16 mx-auto mb-4 text-light-text/30 dark:text-dark-text/30" />
                <p>Profile information could not be loaded or you are not logged in.</p>
            </div>
        );
    }

    const { enrollments, certificates } = profileData;
    const roleBadge = getRoleBadge(user.role);
    const overallProgress = calculateOverallProgress();
    const completedCourses = getCompletedCoursesCount();

    return (
        <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }}
            className="max-w-7xl mx-auto space-y-8"
        >
            {/* Hero Profile Section with Gradient */}
            <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="relative overflow-hidden rounded-3xl"
            >
                <div className={`absolute inset-0 bg-gradient-to-br ${getRoleColor(user.role)} opacity-90`}></div>
                
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>

                <div className="relative z-10 p-8 md:p-12 text-white">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 200 }}
                            className="relative"
                        >
                            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-white/20 backdrop-blur-xl flex items-center justify-center border-4 border-white/30 shadow-2xl">
                                <span className="text-5xl md:text-6xl font-bold">
                                    {user.name.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className={`absolute -bottom-2 -right-2 ${roleBadge.color} p-3 rounded-full border-4 border-white shadow-lg`}>
                                <roleBadge.icon className="h-6 w-6 text-white" />
                            </div>
                        </motion.div>

                        <div className="flex-1 text-center md:text-left">
                            <motion.h1
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="text-4xl md:text-5xl font-bold mb-2"
                            >
                                {user.name}
                            </motion.h1>
                            
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                className="flex flex-wrap items-center gap-4 justify-center md:justify-start mb-4"
                            >
                                {user.userId && (
                                    <div className="flex items-center gap-2 bg-white/30 backdrop-blur-sm px-4 py-2 rounded-full font-mono text-sm border border-white/20">
                                        <UserCircleIcon className="h-4 w-4" />
                                        <span>ID: {user.userId}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                                    <EnvelopeIcon className="h-5 w-5" />
                                    <span>{user.email}</span>
                                </div>
                                <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                                    <CalendarIcon className="h-5 w-5" />
                                    <span>Member since {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.4 }}
                                className="flex flex-wrap gap-3"
                            >
                                <div className="inline-flex items-center gap-2 bg-white/30 backdrop-blur-sm px-6 py-3 rounded-full text-lg font-semibold">
                                    <SparklesIcon className="h-6 w-6" />
                                    {roleBadge.label}
                                </div>
                                <Link
                                    to="/settings"
                                    className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm px-6 py-3 rounded-full text-lg font-semibold transition-all"
                                >
                                    <Cog6ToothIcon className="h-6 w-6" />
                                    Settings
                                </Link>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
                <StatCard
                    icon={AcademicCapIcon}
                    label="Enrolled Courses"
                    value={enrollments.filter(e => e.course && !e.completed && !e.certificateEarned).length}
                    color="from-blue-500 to-cyan-500"
                    delay={0.1}
                />
                <StatCard
                    icon={CheckCircleIcon}
                    label="Completed"
                    value={completedCourses}
                    color="from-green-500 to-emerald-500"
                    delay={0.2}
                />
                <StatCard
                    icon={TrophyIcon}
                    label="Certificates"
                    value={certificates.length}
                    color="from-yellow-500 to-orange-500"
                    delay={0.3}
                />
                <StatCard
                    icon={ArrowTrendingUpIcon}
                    label="Overall Progress"
                    value={`${overallProgress}%`}
                    color="from-purple-500 to-pink-500"
                    delay={0.4}
                />
            </motion.div>

            {/* Tabs */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="card p-2"
            >
                <div className="flex gap-2">
                    <TabButton
                        active={activeTab === 'courses'}
                        onClick={() => setActiveTab('courses')}
                        icon={BookOpenIcon}
                        label="My Courses"
                    />
                    <TabButton
                        active={activeTab === 'certificates'}
                        onClick={() => setActiveTab('certificates')}
                        icon={ShieldCheckIcon}
                        label="Certificates"
                    />
                    <TabButton
                        active={activeTab === 'stats'}
                        onClick={() => setActiveTab('stats')}
                        icon={ChartBarIcon}
                        label="Statistics"
                    />
                </div>
            </motion.div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
                {activeTab === 'courses' && (
                    <CoursesTab
                        enrollments={enrollments.filter(e => e.course)}
                        calculateProgress={calculateProgress}
                    />
                )}
                {activeTab === 'certificates' && (
                    <CertificatesTab certificates={certificates} />
                )}
                {activeTab === 'stats' && (
                    <StatsTab
                        enrollments={enrollments.filter(e => e.course)}
                        certificates={certificates}
                        calculateProgress={calculateProgress}
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// Helper Components
const StatCard = ({ icon: Icon, label, value, color, delay }) => (
    <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay }}
        whileHover={{ y: -5, scale: 1.02 }}
        className="card p-6 relative overflow-hidden group cursor-pointer"
    >
        <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
        <div className="relative z-10">
            <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${color} mb-4`}>
                <Icon className="h-6 w-6 text-white" />
            </div>
            <p className="text-3xl font-bold mb-1">{value}</p>
            <p className="text-sm opacity-70">{label}</p>
        </div>
    </motion.div>
);

const TabButton = ({ active, onClick, icon: Icon, label }) => (
    <button
        onClick={onClick}
        className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
            active
                ? 'bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-500 text-white shadow-lg'
                : 'hover:bg-light-bg dark:hover:bg-dark-bg'
        }`}
    >
        <Icon className="h-5 w-5" />
        <span className="hidden sm:inline">{label}</span>
    </button>
);

const CoursesTab = ({ enrollments, calculateProgress }) => {
    // Enrolled ve Completed kurslar� ay�r
    const completedCourses = enrollments.filter(e => e.completed || e.certificateEarned);
    const enrolledCourses = enrollments.filter(e => !e.completed && !e.certificateEarned);

    return (
        <motion.div
            key="courses"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
        >
            {/* Enrolled Courses Section */}
            <div className="card p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <BookOpenIcon className="h-7 w-7 text-light-primary dark:text-dark-primary" />
                        Enrolled Courses
                    </h2>
                    <span className="text-light-text/70 dark:text-dark-text/70">
                        {enrolledCourses.length} {enrolledCourses.length === 1 ? 'Course' : 'Courses'}
                    </span>
                </div>

                {enrolledCourses.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {enrolledCourses.map((enroll, index) => {
                            const progress = calculateProgress(enroll);
                            const isCompleted = false; // Enrolled courses are not completed
                    
                    return (
                        <motion.div
                            key={enroll._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            whileHover={{ y: -5 }}
                        >
                            <Link
                                to={`/courses/${enroll.course.slug || enroll.course._id}`}
                                className="block border border-light-border dark:border-dark-border rounded-xl overflow-hidden hover:shadow-2xl transition-all bg-light-bg dark:bg-dark-bg group relative"
                            >
                                {isCompleted && (
                                    <div className="absolute top-3 right-3 z-10 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                        <CheckCircleIcon className="h-4 w-4" />
                                        Completed
                                    </div>
                                )}
                                
                                <div className="relative h-48 overflow-hidden">
                                    <img
                                        src={enroll.course.imageUrl || 'https://via.placeholder.com/400x225.png?text=AircoreDynamics'}
                                        alt={`${enroll.course.title} - Course in progress`}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                </div>
                                
                                <div className="p-5">
                                    <h3 className="font-bold text-lg mb-3 line-clamp-2 group-hover:text-light-primary dark:group-hover:text-dark-primary transition-colors">
                                        {enroll.course.title}
                                    </h3>
                                    
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="opacity-70">Progress</span>
                                            <span className="font-semibold">{progress}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${progress}%` }}
                                                transition={{ duration: 1, delay: index * 0.1 }}
                                                className={`h-full rounded-full ${
                                                    isCompleted
                                                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                                        : 'bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-500'
                                                }`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    );
                })}
            </div>
                ) : (
                    <div className="text-center py-16">
                        <BookOpenIcon className="h-16 w-16 mx-auto mb-4 text-light-text/30 dark:text-dark-text/30" />
                        <h3 className="text-xl font-bold mb-2">No Enrolled Courses</h3>
                        <p className="text-light-text/70 dark:text-dark-text/70 mb-6">
                            Start your learning journey by enrolling in a course!
                        </p>
                        <Link to="/courses" className="btn-primary inline-block">
                            Browse Courses
                        </Link>
                    </div>
                )}
            </div>

            {/* Completed Courses Section */}
            <div className="card p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                        <TrophyIcon className="h-7 w-7 text-green-600 dark:text-green-400" />
                        Completed Courses
                    </h2>
                    <span className="text-light-text/70 dark:text-dark-text/70">
                        {completedCourses.length} {completedCourses.length === 1 ? 'Course' : 'Courses'}
                    </span>
                </div>

                {completedCourses.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {completedCourses.map((enroll, index) => {
                            // const progress = 100; // Completed courses are always 100%
                            
                            return (
                                <motion.div
                                    key={enroll._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    whileHover={{ y: -5 }}
                                >
                                    <Link
                                        to={`/courses/${enroll.course.slug || enroll.course._id}`}
                                        className="block border-2 border-green-500/30 dark:border-green-400/30 rounded-xl overflow-hidden hover:shadow-2xl transition-all bg-green-50/50 dark:bg-green-900/10 group relative"
                                    >
                                        <div className="absolute top-3 right-3 z-10 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                                            <CheckCircleIcon className="h-4 w-4" />
                                            Completed
                                        </div>
                                        
                                        <div className="relative h-48 overflow-hidden">
                                            <img
                                                src={enroll.course.imageUrl || 'https://via.placeholder.com/400x225.png?text=AircoreDynamics'}
                                                alt={`${enroll.course.title} - Completed course`}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-green-900/60 to-transparent"></div>
                                        </div>
                                        
                                        <div className="p-5">
                                            <h3 className="font-bold text-lg mb-3 line-clamp-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                                                {enroll.course.title}
                                            </h3>
                                            
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="opacity-70">Status</span>
                                                    <span className="font-semibold text-green-600 dark:text-green-400">Completed</span>
                                                </div>
                                                <div className="w-full bg-green-200 dark:bg-green-900 rounded-full h-2 overflow-hidden">
                                                    <div className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-500 w-full" />
                                                </div>
                                                {enroll.completedAt && (
                                                    <div className="flex items-center gap-1 text-xs opacity-70 mt-2">
                                                        <CalendarIcon className="h-3 w-3" />
                                                        <span>Completed: {new Date(enroll.completedAt).toLocaleDateString()}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <TrophyIcon className="h-16 w-16 mx-auto mb-4 text-light-text/30 dark:text-dark-text/30" />
                        <h3 className="text-xl font-bold mb-2">No Completed Courses Yet</h3>
                        <p className="text-light-text/70 dark:text-dark-text/70">
                            Complete a course and earn a certificate to see it here!
                        </p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

const CertificatesTab = ({ certificates }) => (
    <motion.div
        key="certificates"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="card p-8"
    >
        <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
                <ShieldCheckIcon className="h-7 w-7 text-light-primary dark:text-dark-primary" />
                My Certificates
            </h2>
            <span className="text-light-text/70 dark:text-dark-text/70">
                {certificates.length} {certificates.length === 1 ? 'Certificate' : 'Certificates'}
            </span>
        </div>

        {certificates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {certificates.map((cert, index) => (
                    <motion.div
                        key={cert._id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        className="relative overflow-hidden rounded-xl border-2 border-light-border dark:border-dark-border bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 p-6 group"
                    >
                        <div className="absolute top-0 right-0 opacity-10 group-hover:opacity-20 transition-opacity">
                            <TrophyIcon className="h-32 w-32 text-yellow-500" />
                        </div>
                        
                        <div className="relative z-10">
                            <div className="flex items-start justify-between mb-4">
                                <div className="bg-gradient-to-br from-yellow-500 to-orange-500 p-3 rounded-xl">
                                    <ShieldCheckIcon className="h-8 w-8 text-white" />
                                </div>
                                <span className="text-xs bg-white dark:bg-dark-bg px-3 py-1 rounded-full font-semibold">
                                    #{cert.certificateId}
                                </span>
                            </div>
                            
                            <h3 className="font-bold text-lg mb-2">{cert.course.title}</h3>
                            
                            <div className="flex items-center gap-2 text-sm opacity-70 mb-4">
                                <CalendarIcon className="h-4 w-4" />
                                <span>Issued: {new Date(cert.issuedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                            </div>
                            
                            <Link
                                to={`/verify/${cert.certificateId}`}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all font-semibold"
                            >
                                View Certificate
                                <ArrowTrendingUpIcon className="h-4 w-4" />
                            </Link>
                        </div>
                    </motion.div>
                ))}
            </div>
        ) : (
            <div className="text-center py-16">
                <TrophyIcon className="h-16 w-16 mx-auto mb-4 text-light-text/30 dark:text-dark-text/30" />
                <h3 className="text-xl font-bold mb-2">No Certificates Yet</h3>
                <p className="text-light-text/70 dark:text-dark-text/70">
                    Complete courses to earn certificates!
                </p>
            </div>
        )}
    </motion.div>
);

const StatsTab = ({ enrollments, certificates, calculateProgress }) => {
    // Completed: Sertifika al�nm�� kurslar (%100)
    const completedCourses = enrollments.filter(e => e.completed || e.certificateEarned).length;
    
    // In Progress: Ba�lanm�� ama tamamlanmam�� kurslar (%1-99)
    const inProgressCourses = enrollments.filter(e => {
        if (e.completed || e.certificateEarned) return false; // Tamamlanm��lar� dahil etme
        const progress = calculateProgress(e);
        return progress > 0 && progress < 100; // %1 ile %99 aras�
    }).length;
    
    // Not Started: Enroll olunmu� ama hi� ba�lanmam�� kurslar (%0)
    const notStartedCourses = enrollments.filter(e => {
        if (e.completed || e.certificateEarned) return false; // Tamamlanm��lar� dahil etme
        const progress = calculateProgress(e);
        return progress === 0; // Hi� ba�lanmam��
    }).length;

    return (
        <motion.div
            key="stats"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
        >
            <div className="card p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                    <ChartBarIcon className="h-7 w-7 text-light-primary dark:text-dark-primary" />
                    Learning Statistics
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="text-center p-6 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-2 border-green-500/20">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <CheckCircleIcon className="h-8 w-8 text-green-500" />
                        </div>
                        <div className="text-4xl font-bold text-green-500 mb-2">{completedCourses}</div>
                        <p className="font-semibold mb-1">Completed</p>
                        <p className="text-xs opacity-60">100% - Certificate earned</p>
                    </div>
                    <div className="text-center p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-2 border-blue-500/20">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <ArrowTrendingUpIcon className="h-8 w-8 text-blue-500" />
                        </div>
                        <div className="text-4xl font-bold text-blue-500 mb-2">{inProgressCourses}</div>
                        <p className="font-semibold mb-1">In Progress</p>
                        <p className="text-xs opacity-60">1-99% - Keep learning!</p>
                    </div>
                    <div className="text-center p-6 rounded-xl bg-gradient-to-br from-gray-500/10 to-gray-600/10 border-2 border-gray-500/20">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <BookOpenIcon className="h-8 w-8 text-gray-500" />
                        </div>
                        <div className="text-4xl font-bold text-gray-500 mb-2">{notStartedCourses}</div>
                        <p className="font-semibold mb-1">Not Started</p>
                        <p className="text-xs opacity-60">0% - Ready to begin</p>
                    </div>
                </div>

                <div className="space-y-4">
                    <h3 className="font-bold text-lg mb-4">Course Progress Breakdown</h3>
                    {enrollments.length > 0 ? (
                        enrollments.map((enroll, index) => {
                            const progress = calculateProgress(enroll);
                            return (
                                <div key={enroll._id} className="p-4 rounded-lg bg-light-bg dark:bg-dark-bg">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="font-semibold">{enroll.course.title}</span>
                                        <span className="text-sm font-bold">{progress}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${progress}%` }}
                                            transition={{ duration: 1, delay: index * 0.1 }}
                                            className={`h-full rounded-full ${
                                                progress === 100
                                                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                                    : progress > 0
                                                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                                                    : 'bg-gray-400'
                                            }`}
                                        />
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <p className="text-center opacity-70 py-8">No course data available</p>
                    )}
                </div>
            </div>

            <div className="card p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-2 border-purple-500/20">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <FireIcon className="h-6 w-6 text-purple-500" />
                    Achievements
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <AchievementBadge
                        icon={StarIcon}
                        label="First Course"
                        unlocked={enrollments.length >= 1}
                    />
                    <AchievementBadge
                        icon={TrophyIcon}
                        label="First Certificate"
                        unlocked={certificates.length >= 1}
                    />
                    <AchievementBadge
                        icon={FireIcon}
                        label="5 Courses"
                        unlocked={enrollments.length >= 5}
                    />
                    <AchievementBadge
                        icon={SparklesIcon}
                        label="Master"
                        unlocked={completedCourses >= 10}
                    />
                </div>
            </div>
        </motion.div>
    );
};

const AchievementBadge = ({ icon: Icon, label, unlocked }) => (
    <div className={`text-center p-4 rounded-xl ${unlocked ? 'bg-yellow-500/20' : 'bg-gray-500/10'}`}>
        <Icon className={`h-8 w-8 mx-auto mb-2 ${unlocked ? 'text-yellow-500' : 'text-gray-400'}`} />
        <p className={`text-xs font-semibold ${unlocked ? '' : 'opacity-50'}`}>{label}</p>
    </div>
);

export default ProfilePage;
